// Settings UI — admins can create/manage portals and their request types.
// Portals are the org-level branded help desks (IT, HR, Facilities, …).
// Each portal has its own ticket counter and key (e.g., IT → IT-1, IT-2).
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Lifebuoy, Plus, Trash, ArrowLeft, DownloadSimple, Plugs, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import JsmImportDialog from '../../components/JsmImportDialog';

const fmtBytes = (b) => {
  if (!b || b <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

export default function ServiceDeskSettingsPage() {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [showJsmImport, setShowJsmImport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletionLog, setDeletionLog] = useState([]);

  const sdEnabled = caps?.service_desk_enabled === true;
  const isAdmin = ['owner', 'admin'].includes(currentOrg?.role);

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() });
      setPortals(data || []);
      try {
        const { data: log } = await axios.get(`${API}/service-desk/deletion-log`, { withCredentials: true, headers: getOrgHeaders() });
        setDeletionLog(log || []);
      } catch (_) {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (!isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-500">Admins only.</p>
      </div>
    );
  }

  const createPortal = async () => {
    if (!newName.trim()) return toast.error('Name is required');
    if (!/^[A-Z][A-Z0-9]{1,9}$/.test(newKey.trim())) return toast.error('Key must be 2-10 uppercase letters/digits, starting with a letter');
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/portals`,
        { name: newName.trim(), key: newKey.trim(), description: newDesc.trim() },
        { withCredentials: true, headers: getOrgHeaders() });
      setPortals(prev => [...prev, data]);
      setShowNew(false);
      setNewName(''); setNewKey(''); setNewDesc('');
      toast.success(`Portal "${data.name}" created`);
      window.dispatchEvent(new CustomEvent('sd-portals-changed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create portal');
    } finally { setCreating(false); }
  };

  const openDeleteDialog = async (portal) => {
    setDeleteTarget(portal);
    setDeletePreview(null);
    setDeleteConfirm('');
    try {
      const { data } = await axios.get(`${API}/service-desk/portals/${portal.portal_id}/delete-preview`,
        { withCredentials: true, headers: getOrgHeaders() });
      setDeletePreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load delete preview');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/service-desk/portals/${deleteTarget.portal_id}`, {
        withCredentials: true,
        headers: getOrgHeaders(),
        data: { confirm_key: deleteConfirm.trim() },
      });
      setPortals(prev => prev.filter(p => p.portal_id !== deleteTarget.portal_id));
      toast.success(`Portal "${deleteTarget.name}" permanently deleted`);
      setDeleteTarget(null);
      window.dispatchEvent(new CustomEvent('sd-portals-changed'));
      try {
        const { data: log } = await axios.get(`${API}/service-desk/deletion-log`, { withCredentials: true, headers: getOrgHeaders() });
        setDeletionLog(log || []);
      } catch (_) {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="sd-settings-page">
      <div className="max-w-5xl mx-auto px-8 py-6">
        <button onClick={() => navigate('/service-desk')}
          className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Back to queue
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
              <Lifebuoy size={14} weight="fill" /> Service Desk
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Settings</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Manage portals and request types</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowJsmImport(true)} data-testid="sd-jsm-import-btn">
              <DownloadSimple size={14} className="mr-1.5" /> Import from Jira
            </Button>
            <Button onClick={() => setShowNew(true)} data-testid="sd-new-portal-btn">
              <Plus size={14} className="mr-1.5" /> New portal
            </Button>
          </div>
        </div>

        {loading ? <div className="text-center py-8 text-zinc-400">Loading…</div> : (
          <div className="space-y-3">
            {portals.length === 0 && (
              <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-sm text-zinc-500">
                No portals yet. Click "New portal" to get started.
              </div>
            )}
            {portals.map(p => (
              <div key={p.portal_id} className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center gap-3"
                data-testid={`sd-portal-row-${p.portal_id}`}>
                <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center text-blue-700 font-bold text-sm ring-1 ring-blue-100">
                  {p.key}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-zinc-900 text-sm">{p.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.description || 'No description'}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{p.ticket_counter || 0} tickets</p>
                </div>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/service-desk/settings/portal/${p.portal_id}`)}
                    data-testid={`sd-portal-manage-${p.portal_id}`}>
                    Configure
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(p)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`sd-portal-delete-${p.portal_id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {deletionLog.length > 0 && (
          <div className="mt-8" data-testid="sd-deletion-log">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">
              <ClockCounterClockwise size={13} /> Recently deleted
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
              {deletionLog.map(l => (
                <div key={l.log_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`sd-deletion-log-${l.log_id}`}>
                  <div className="w-8 h-8 rounded-md bg-zinc-100 flex items-center justify-center text-zinc-400 font-bold text-[11px] shrink-0">
                    {l.portal_key}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-zinc-700 truncate">
                      {l.portal_name} <span className="text-zinc-400 font-normal">deleted</span>
                    </p>
                    <p className="text-[11px] text-zinc-400">
                      by {l.deleted_by_name || 'someone'} · {l.tickets_removed} tickets, {l.files_removed} files
                      {l.deleted_at ? ` · ${new Date(l.deleted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent className="max-w-md" data-testid="sd-new-portal-dialog">
            <DialogHeader><DialogTitle>New portal</DialogTitle></DialogHeader>
            <button
              type="button"
              onClick={() => { setShowNew(false); setTimeout(() => setShowJsmImport(true), 160); }}
              data-testid="sd-new-portal-import-jsm"
              className="w-full flex items-center justify-between gap-3 rounded-xl border border-orange-200 bg-orange-50/60 hover:bg-orange-50 hover:border-orange-300 transition-colors px-3.5 py-2.5 text-left mb-1"
            >
              <span className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                  <Plugs size={16} weight="duotone" />
                </span>
                <span>
                  <span className="block text-[12.5px] font-bold text-zinc-900">Migrating from Jira Service Management?</span>
                  <span className="block text-[10.5px] text-zinc-500">Import your JSM project — tickets & requests come with it.</span>
                </span>
              </span>
              <span className="text-[11px] font-semibold text-orange-600 whitespace-nowrap">Import →</span>
            </button>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Portal name</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., IT Support"
                  data-testid="sd-new-portal-name" />
              </div>
              <div>
                <Label className="text-xs">Key (used in ticket numbers like IT-1, IT-2)</Label>
                <Input value={newKey} onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                  placeholder="IT" maxLength={10} data-testid="sd-new-portal-key" />
                <p className="text-[10px] text-zinc-400 mt-1">2–10 letters/digits, starting with a letter (auto-uppercased).</p>
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Textarea rows={2} value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="What requests does this portal handle?" data-testid="sd-new-portal-desc" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button onClick={createPortal} disabled={creating} data-testid="sd-new-portal-submit">
                {creating ? 'Creating…' : 'Create portal'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="sd-delete-portal-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash size={18} weight="fill" /> Permanently delete portal
              </DialogTitle>
              <DialogDescription>This permanently erases the portal and all of its tickets, comments and attachments. This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <p className="text-sm text-zinc-600">
                This will <span className="font-semibold text-zinc-900">permanently erase</span> the
                {' '}<span className="font-semibold text-zinc-900">{deleteTarget?.name}</span> portal and everything under it.
                {' '}<span className="font-semibold">This cannot be undone.</span>
              </p>
              <div className="rounded-lg border border-red-200 bg-red-50/70 px-3.5 py-3 text-[13px] text-zinc-700" data-testid="sd-delete-preview">
                {!deletePreview ? (
                  <span className="text-zinc-400">Calculating what will be removed…</span>
                ) : (
                  <ul className="space-y-1">
                    <li className="flex justify-between"><span>Request types</span><span className="font-semibold text-zinc-900">{deletePreview.request_type_count}</span></li>
                    <li className="flex justify-between"><span>Tickets</span><span className="font-semibold text-zinc-900">{deletePreview.ticket_count}</span></li>
                    <li className="flex justify-between"><span>Comments</span><span className="font-semibold text-zinc-900">{deletePreview.comment_count}</span></li>
                    <li className="flex justify-between"><span>Attachments &amp; images</span><span className="font-semibold text-zinc-900">{deletePreview.attachment_count} ({fmtBytes(deletePreview.attachment_bytes)})</span></li>
                  </ul>
                )}
              </div>
              <div>
                <Label className="text-xs">Type the portal key <span className="font-mono font-bold text-zinc-900">{deleteTarget?.key}</span> to confirm</Label>
                <Input
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value.toUpperCase())}
                  placeholder={deleteTarget?.key}
                  className="font-mono"
                  autoFocus
                  data-testid="sd-delete-confirm-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting || !deletePreview || deleteConfirm.trim().toUpperCase() !== String(deleteTarget?.key || '').toUpperCase()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="sd-delete-confirm-btn"
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <JsmImportDialog
          open={showJsmImport}
          onClose={() => { setShowJsmImport(false); load(); }}
          onOpenPortal={(portalId) => navigate(`/service-desk/settings/portal/${portalId}`)}
        />
      </div>
    </div>
  );
}
